import { JSX, For, Show, Switch, Match } from 'solid-js';

import Empty from './Empty';

type TableColumn<T extends Record<string, unknown>> = {
  key: keyof T;
  title: JSX.Element;
  align?: 'left' | 'center' | 'right';
  width?: string;
  render?: (record: T, index: number) => JSX.Element;
};

type TableProps<T extends Record<string, unknown>> = {
  columns: TableColumn<T>[];
  data: T[];
  onClickTr?: (record: T) => void;
  className?: string;
  emptyText?: JSX.Element;
  loading?: boolean;
  classes?: {
    table?: string;
    thead?: string;
    tbody?: string;
    tr?: string;
    td?: string;
  };
};

function Table<T extends Record<string, unknown>>(props: TableProps<T>) {
  const getAlignClass = (align: string = 'left') => {
    switch (align) {
      case 'center':
        return 'text-center';
      case 'right':
        return 'text-right';
      default:
        return 'text-left';
    }
  };

  const renderCell = (column: TableColumn<T>, record: T, index: number) => {
    const value = record[column.key as keyof T];

    return (
      <Show when={column.render} fallback={<span>{String(value ?? '')}</span>}>
        {column.render!(record, index)}
      </Show>
    );
  };

  return (
    <div class={props.className || ''}>
      <table class={cx('w-full rounded-lg', props.classes?.table)}>
        <thead class={cx('bg-alpha-neutral-autowhite-a5', props.classes?.thead)}>
          <tr>
            <For each={props.columns}>
              {(column) => (
                <th
                  class={`px-4 py-2 ${getAlignClass(column.align)} font-normal`}
                  style={column.width ? { width: column.width } : {}}
                >
                  {column.title}
                </th>
              )}
            </For>
          </tr>
        </thead>

        <tbody class={cx('divide-y', props.classes?.tbody)}>
          <Switch>
            <Match when={props.loading}>
              <tr>
                <td colspan={props.columns.length} class="px-4 py-3">
                  <div class="flex center">
                    <BarLoading />
                  </div>
                </td>
              </tr>
            </Match>

            <Match when={props.data.length === 0}>
              <tr>
                <td colspan={props.columns.length}>
                  <Empty class="!bg-layer3-card">{props.emptyText}</Empty>
                </td>
              </tr>
            </Match>

            <Match when={props.data.length > 0}>
              <For each={props.data}>
                {(record, index) => (
                  <tr
                    class={cx(
                      'bg-layer3-card border-main3',
                      props.classes?.tr,
                      props.onClickTr && 'cursor-pointer hover:bg-layer4'
                    )}
                    onClick={() => props.onClickTr?.(record)}
                  >
                    <For each={props.columns}>
                      {(column) => (
                        <td
                          class={cx(`p-4 ${getAlignClass(column.align)}`, props.classes?.td)}
                          style={column.width ? { width: column.width } : {}}
                        >
                          {renderCell(column, record, index())}
                        </td>
                      )}
                    </For>
                  </tr>
                )}
              </For>
            </Match>
          </Switch>
        </tbody>
      </table>
    </div>
  );
}

// 使用示例：
// type MyTableProps = ComponentProps<typeof Table>;
// type MyTableColumn<T> = MyTableProps['columns'][0];
// 辅助函数：创建带类型推导的columns
const createColumns = <T extends Record<string, unknown>>(
  columns: Array<{
    key: keyof T;
    title: JSX.Element;
    align?: 'left' | 'center' | 'right';
    width?: string;
    render?: (record: T, index?: number) => JSX.Element;
  }>
) => columns;

// 将辅助函数添加为Table的静态方法
Table.createColumns = createColumns;

// 扩展Table类型以包含静态方法
type TableWithStatic = typeof Table & {
  createColumns: typeof createColumns;
};

export default Table as TableWithStatic;
